'use client';

import { useMemo, useState } from 'react';
import {
  computeLineDiff,
  SAMPLE_DIFF_CHANGED,
  SAMPLE_DIFF_ORIGINAL,
  type LineDiffRow,
} from '@/lib/browser-textanalysis-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const ADD_BG = 'rgba(34, 197, 94, 0.14)';
const REMOVE_BG = 'rgba(239, 68, 68, 0.14)';
const ADD_INLINE = 'rgba(34, 197, 94, 0.35)';
const REMOVE_INLINE = 'rgba(239, 68, 68, 0.32)';

function renderRow(row: LineDiffRow, index: number) {
  const background = row.op === 'add' ? ADD_BG : row.op === 'remove' ? REMOVE_BG : 'transparent';
  const sign = row.op === 'add' ? '+' : row.op === 'remove' ? '-' : ' ';
  const signColor =
    row.op === 'add' ? '#16a34a' : row.op === 'remove' ? '#dc2626' : 'var(--muted, #94a3b8)';

  return (
    <div
      key={index}
      style={{
        display: 'grid',
        gridTemplateColumns: '3.5rem 1.25rem 1fr',
        gap: '0.5rem',
        padding: '0.1rem 0.35rem',
        background,
        alignItems: 'baseline',
      }}
    >
      <span
        style={{
          color: 'var(--muted, #94a3b8)',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontSize: '0.75rem',
          userSelect: 'none',
        }}
      >
        {row.oldNumber ?? ''}
        {' / '}
        {row.newNumber ?? ''}
      </span>
      <span style={{ color: signColor, fontWeight: 700, userSelect: 'none' }}>{sign}</span>
      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {row.segments && row.segments.length > 0
          ? row.segments.map((seg, i) => (
              <span
                key={i}
                style={{
                  background:
                    seg.op === 'add'
                      ? ADD_INLINE
                      : seg.op === 'remove'
                        ? REMOVE_INLINE
                        : 'transparent',
                  borderRadius: '2px',
                }}
              >
                {seg.text}
              </span>
            ))
          : row.text || ' '}
      </span>
    </div>
  );
}

export default function BrowserTextDiffChecker() {
  const [original, setOriginal] = useState('');
  const [changed, setChanged] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const diff = useMemo(() => computeLineDiff(original, changed), [original, changed]);
  const hasInput = original.length > 0 || changed.length > 0;

  async function handleCopy() {
    const text = diff.rows
      .map((row) => {
        const prefix = row.op === 'add' ? '+ ' : row.op === 'remove' ? '- ' : '  ';
        return prefix + row.text;
      })
      .join('\n');
    try {
      await copyText(text);
      setCopyMessage('Diff copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the diff.');
    }
  }

  function loadSample() {
    setOriginal(SAMPLE_DIFF_ORIGINAL);
    setChanged(SAMPLE_DIFF_CHANGED);
    setCopyMessage(null);
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Text diff</span>
        <h2 className="tool-live-title">Text Diff Checker</h2>
        <p className="tool-live-subtitle">
          Compare two blocks of text and see exactly what was added, removed, or changed, line by
          line and word by word.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Original text</span>
              <textarea
                onChange={(e) => setOriginal(e.target.value)}
                placeholder="Paste the original version here..."
                rows={8}
                value={original}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Changed text</span>
              <textarea
                onChange={(e) => setChanged(e.target.value)}
                placeholder="Paste the changed version here..."
                rows={8}
                value={changed}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={loadSample} type="button">
              Load sample
            </button>
            <button
              className="button button-secondary"
              disabled={!hasInput}
              onClick={handleCopy}
              type="button"
            >
              Copy diff
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              display: 'flex',
              gap: '1.25rem',
              flexWrap: 'wrap',
              marginBottom: '0.75rem',
            }}
          >
            <span style={{ color: '#16a34a', fontWeight: 600 }}>+{diff.added} added</span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>-{diff.removed} removed</span>
            <span style={{ color: 'var(--muted, #94a3b8)' }}>{diff.unchanged} unchanged</span>
          </div>
          <div
            style={{
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
              fontSize: '0.82rem',
              lineHeight: 1.5,
              maxHeight: '28rem',
              overflow: 'auto',
              border: '1px solid var(--border, rgba(148,163,184,0.25))',
              borderRadius: '0.5rem',
              padding: '0.5rem 0',
            }}
          >
            {hasInput ? (
              diff.rows.map((row, index) => renderRow(row, index))
            ) : (
              <p style={{ padding: '0.75rem', color: 'var(--muted, #94a3b8)' }}>
                Enter text in both fields to see the differences highlighted here.
              </p>
            )}
          </div>
          <p className="tool-live-panel-note">Compared locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
